import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useTheme
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    Apps as AppsIcon
} from '@mui/icons-material';

interface Role {
    id: string;
    ident: string;
    name: string;
    clientId?: string;
}

interface RoleCardProps {
    role: Role;
    isSystemRole: boolean;
    onEdit?: (role: Role) => void;
    onDelete?: (role: Role) => void;
    onManageClientRoles?: (clientId: string, clientName: string) => void;
    clientName?: string;
}

export const RoleCard: React.FC<RoleCardProps> = ({
    role,
    isSystemRole,
    onEdit,
    onDelete,
    onManageClientRoles,
    clientName
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        onEdit?.(role);
        handleClose();
    };

    const handleDelete = () => {
        onDelete?.(role);
        handleClose();
    };

    const handleManageClientRoles = () => {
        if (role.clientId) {
            onManageClientRoles?.(role.clientId, role.name);
        }
        handleClose();
    };

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isSystemRole ? (
                        <SecurityIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                    ) : (
                        <AppsIcon sx={{ color: theme.palette.semantic!.warning, fontSize: 24 }} />
                    )}
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                        {role.name}
                    </Typography>
                </Box>
                    <IconButton
                        size="small"
                        onClick={handleClick}
                        aria-label="more"
                        aria-controls={open ? 'role-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Identifikator: <code>{role.ident}</code>
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                        label={isSystemRole ? 'System-Rolle' : 'Client-Rolle'} 
                        color={isSystemRole ? 'primary' : 'secondary'}
                        size="small"
                        variant="filled"
                    />
                    {!isSystemRole && (
                        <Chip 
                            label={`Client: ${clientName || role.clientId}`} 
                            variant="outlined"
                            size="small"
                            color="default"
                        />
                    )}
                </Box>
            </CardContent>

            <Menu
                id="role-menu"
                MenuListProps={{
                    'aria-labelledby': 'role-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {onEdit && (
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Bearbeiten</ListItemText>
                    </MenuItem>
                )}
                {onDelete && (
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Löschen</ListItemText>
                    </MenuItem>
                )}
                {!isSystemRole && onManageClientRoles && (
                    <MenuItem onClick={handleManageClientRoles}>
                        <ListItemIcon>
                            <SecurityIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Client-Rollen verwalten</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Card>
    );
};
